import {
  Component,
  h,
  Prop,
  Element,
  Host,
  State,
  Watch,
  Event,
  EventEmitter,
} from '@stencil/core';
import { debounce } from '../../utils/throttle';
import type { GridSizes } from '../../interface';

const STATEOPTS = ['col-span', 'col-start', 'row-span', 'row-start'];

/**
 * A context-aware CSS grid implementation.
 * Uses it's own width to choose column number - not screen width.
 */
@Component({
  tag: 'nano-grid',
  styleUrl: 'grid.scss',
  shadow: true,
})
export class Grid {
  @Element() private el: HTMLNanoGridElement;
  private ro: ResizeObserver;
  private generalClasses: Array<string> = [''];
  private gridClass: string = null;
  private isSizes: GridSizes[] = [null];
  private currentWidth: number;

  constructor() {
    this.stateChange = debounce(this.stateChange.bind(this), 100);
  }

  @State() ready: boolean = false;
  @State() isSmall = false;
  @State() isMedium = false;
  @State() isLarge = false;
  @State() isXL = false;
  @State() isXXL = false;

  /**
   * the component's small breakpoint. Note - all breakpoints are based on component width, not screen width.
   */
  @Prop() sSize: number = 300;

  /**
   * the component's medium breakpoint. Note - all breakpoints are based on component width, not screen width.
   */
  @Prop() mSize: number = 550;

  /**
   * the component's large breakpoint. Note - all breakpoints are based on component width, not screen width.
   */
  @Prop() lSize: number = 800;

  /**
   * the component's xl breakpoint. Note - all breakpoints are based on component width, not screen width.
   */
  @Prop() xlSize: number = 1000;

  /**
   * the number of columns the grid has at the small breakpoint.
   */
  @Prop() sCols: number;

  /**
   * the number of columns the grid has at the medium breakpoint.
   */
  @Prop() mCols: number;

  /**
   * the number of columns the grid has at the large breakpoint.
   */
  @Prop() lCols: number;

  /**
   * the number of columns the grid has at the xl breakpoint.
   */
  @Prop() xlCols: number;

  /**
   * the number of columns the grid has at the xxl breakpoint (anything greater than the xl breakpoint)
   */
  @Prop() xxlCols: number;

  /**
   * shows a grid helper to visualise where columns are
   */
  @Prop({ reflect: true }) showHelper: boolean = false;

  /**
   * shortcut for a traditional website like grid (requires more than one row). The second row will auto expand to fill the grid height. The first row and > 2nd row will be the size of their content. e.g. header, body and footer.
   */
  @Prop({ reflect: true }) contentPanel: boolean = false;

  /**
   * Helper to make grid items expand to full height in IE11
   */
  @Prop({ reflect: true }) fullHeight: boolean = false;

  /**
   * Emitted when the `nano-grid` changes breakpoint
   */
  @Event() nanoBpChange: EventEmitter<string[]>;

  @Watch('sSize')
  @Watch('mSize')
  @Watch('lSize')
  @Watch('xlSize')
  @Watch('sCols')
  @Watch('mCols')
  @Watch('lCols')
  @Watch('xlCols')
  @Watch('xxlCols')
  propChanged() {
    this.applySizeClasses();
  }

  private applySizeClasses() {
    const size = this.currentWidth;

    this.isSmall = false;
    this.isMedium = false;
    this.isLarge = false;
    this.isXL = false;
    this.isXXL = false;

    this.generalClasses = [];
    this.gridClass = null;
    this.isSizes = [null];

    this.isSmall = true;
    this.generalClasses.push('is-small');
    if (this.sCols) this.gridClass = `nano-grid-${this.sCols}`;
    this.isSizes.push({ size: 's', active: true });

    if (size > this.sSize) {
      this.isMedium = true;
      this.generalClasses.push('is-medium');
      if (this.mCols) this.gridClass = `nano-grid-${this.mCols}`;
      this.isSizes.push({ size: 'm', active: true });
    }

    if (size > this.mSize) {
      this.isLarge = true;
      this.generalClasses.push('is-large');
      if (this.lCols) this.gridClass = `nano-grid-${this.lCols}`;
      this.isSizes.push({ size: 'l', active: true });
    }

    if (size > this.lSize) {
      this.isXL = true;
      this.generalClasses.push('is-xl');
      if (this.xlCols) this.gridClass = `nano-grid-${this.xlCols}`;
      this.isSizes.push({ size: 'xl', active: true });
    }

    if (size > this.xlSize) {
      this.isXXL = true;
      this.generalClasses.push('is-xxl');
      if (this.xxlCols) this.gridClass = `nano-grid-${this.xxlCols}`;
      this.isSizes.push({ size: 'xxl', active: true });
    }

    this.el.style.setProperty(
      '--current-grid-size',
      `'grid size: ${this.isSizes.slice().pop().size}'`
    );
    this.applyChildrenClasses();
    setTimeout(() => (this.ready = true), 0);
  }

  @Watch('isSmall')
  @Watch('isMedium')
  @Watch('isLarge')
  @Watch('isXL')
  @Watch('isXXL')
  stateChange() {
    this.nanoBpChange.emit(this.generalClasses);
  }

  private applyChildrenClasses() {
    const gridItems = this.el.querySelectorAll('nano-grid-item');
    if (gridItems.length) {
      gridItems.forEach((gridItem: HTMLNanoGridItemElement) => {
        gridItem.changeBP(this.isSizes);
      });
      return;
    }

    // this logic has been put into grid-item. Keep here for legacy for now
    let stateArr: Array<string> = [''];
    let itemState: string = '';
    let found: any;
    let classes: Array<string>;

    const ctx = this.el;

    // item states come in 4 possible flavours at every breakpoint. E.g.
    // xl-col-span-2
    // xl-col-start-2
    // xl-row-span-2
    // xl-row-start-2

    // loop through all grid items with states.
    [].map.call(ctx.children, (gItem: HTMLElement) => {
      if (!gItem.hasAttribute('grid-states')) return;

      // gridItems.forEach(gItem => {

      // clear all previous grid state classnames
      classes = gItem.className
        .split(' ')
        .filter((c) => !c.startsWith('nano-grid-'));
      gItem.className = classes.join(' ').trim();

      // get all potential states this element can have
      const itemStates = gItem.getAttribute('grid-states').split(' ');

      // loop through all potential state options: col & row span & start
      STATEOPTS.forEach((stateOpt) => {
        // reset found flag
        found = false;

        // loop through all the current valid breakpoints / sizes backwards because
        // we only care about the item's state at the largest current breakpoint.
        this.isSizes
          .slice()
          .reverse()
          .forEach(function (size) {
            if (found || !size) return;
            found = itemStates.find(
              (state) => state.indexOf(`${size.size}-${stateOpt}`) === 0
            );

            // found a state at this current size. apply class.
            if (found) {
              // clear previous grid state classname
              classes = gItem.className
                .split(' ')
                .filter((c) => !c.includes('nano-grid-${stateOpt}'));
              gItem.className = classes.join(' ').trim();

              stateArr = found.split('-');

              // remove the size of the state
              stateArr.shift();
              itemState = stateArr.join('-');

              // add new class
              gItem.classList.add(`nano-grid-${itemState}`);
            }
          });
      });
    });
  }

  componentWillLoad() {
    const ctx = this.el;
    [].map.call(ctx.children, (ele: HTMLElement) =>
      ele.classList.add('nano-griditem')
    );
  }

  componentDidLoad() {
    this.ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (!entry.contentRect.width) return;
        this.currentWidth = entry.contentRect.width;
        this.applySizeClasses();
      }
    });
    this.ro.observe(this.el);
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
  }

  render() {
    return (
      <Host
        class={{
          [this.generalClasses.join(' ')]: true,
          'has-grid': !!this.gridClass,
          ready: this.ready,
          [this.gridClass]: true,
        }}
      >
        <div
          class={{
            grid: true,
          }}
        >
          <slot />
        </div>
      </Host>
    );
  }
}
